import { useMemo } from 'react';
import {
  DataTable,
  Icon,
  InlineStack,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  SkeletonText,
  Stack,
  StatusIndicator,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import { createColumnHelper } from '@tanstack/table-core';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useEnvHealthPush, useWorkspaceHealthSimulation } from '@/features/workspace-health';
import { useActiveHealthTab } from '../hooks';
import type {
  ServicesApiEnvHealthPushConfigItem,
  ServicesApiEnvHealthPushMetricTrend,
  ServicesApiEnvHealthPushPlatformTrends,
} from '@/services/api/services-api';

function ConfigIcon({ status }: { status: ServicesApiEnvHealthPushConfigItem['status'] }) {
  if (status === 'ok') {
    return <StatusIndicator color="green" />;
  } else if (status === 'missing') {
    return <StatusIndicator color="red" />;
  }
  return <StatusIndicator color="yellow" />;
}

function trendIndicator(direction: ServicesApiEnvHealthPushMetricTrend['direction']): {
  icon: 'arrow-up' | 'arrow-down' | 'no-change';
  color: 'success' | 'critical' | 'subtle';
} {
  if (direction === 'improving') {
    return { icon: 'arrow-up', color: 'success' };
  } else if (direction === 'declining') {
    return { icon: 'arrow-down', color: 'critical' };
  }
  return { icon: 'no-change', color: 'subtle' };
}

function MetricCell({ metric }: { metric: ServicesApiEnvHealthPushMetricTrend }) {
  if (!metric.active) {
    return (
      <Text size="xs" color="subtle">
        —
      </Text>
    );
  }
  const trend = trendIndicator(metric.direction);
  return (
    <Stack gap="0">
      <Text size="s" weight="semibold">
        {metric.current}
      </Text>
      <InlineStack gap="025" alignBlock="center">
        <Icon name={trend.icon} size="xs" color={trend.color} />
        <Text size="xs" color="subtle">
          {metric.previous}
        </Text>
      </InlineStack>
    </Stack>
  );
}

const columnHelper = createColumnHelper<ServicesApiEnvHealthPushPlatformTrends>();

export function PushHealthPanel() {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { activeTab } = useActiveHealthTab();
  const { simulate } = useWorkspaceHealthSimulation();
  const pushQuery = useEnvHealthPush(workspaceId, simulate, activeTab === 'messaging');
  const data = pushQuery.data;

  const columns = useMemo(
    () => [
      columnHelper.accessor('platform', {
        header: 'Platform',
        cell: (info) => <Text size="s">{info.getValue()}</Text>,
      }),
      columnHelper.display({
        id: 'delivery_rate',
        header: 'Delivery',
        cell: (info) => <MetricCell metric={info.row.original.metrics.delivery_rate} />,
      }),
      columnHelper.display({
        id: 'bounce_rate',
        header: 'Bounce',
        cell: (info) => <MetricCell metric={info.row.original.metrics.bounce_rate} />,
      }),
      columnHelper.display({
        id: 'open_rate',
        header: 'Open',
        cell: (info) => <MetricCell metric={info.row.original.metrics.open_rate} />,
      }),
    ],
    [],
  );

  return (
    <Panel style={{ height: '100%' }}>
      <PanelHeader>
        <PanelTitle>Push Notifications</PanelTitle>
      </PanelHeader>
      <PanelSection>
        {pushQuery.isLoading ? (
          <SkeletonText size="s" lines={3} />
        ) : data == null ? (
          <InlineStack gap="100" alignBlock="center">
            <Icon name="error" size="sm" color="critical" />
            <Text size="s" color="subtle">
              Unable to load push health
            </Text>
          </InlineStack>
        ) : (
          <Stack gap="200">
            <Stack gap="100">
              {data.configuration.checks.map((check, index) => (
                <InlineStack key={index} gap="100" alignBlock="center">
                  <ConfigIcon status={check.status} />
                  <Text size="s">{check.name}</Text>
                  <Tooltip content={check.detail}>
                    <Icon name="help" size="xs" color="subtle" />
                  </Tooltip>
                </InlineStack>
              ))}
            </Stack>

            {data.platforms.length > 0 ? (
              <Stack gap="150">
                <Text size="xs" color="subtle">
                  {data.period}
                </Text>
                <DataTable data={data.platforms} columns={columns} caption="Push delivery trends" />
              </Stack>
            ) : (
              <InlineStack gap="100" alignBlock="center">
                <Icon name="info" size="sm" color="subtle" />
                <Text size="s" color="subtle">
                  No push sends in the last 7 days
                </Text>
              </InlineStack>
            )}
          </Stack>
        )}
      </PanelSection>
    </Panel>
  );
}
